import { useEffect, useState } from "react";

import {
  applyTheme,
  getThemePref,
  resolveTheme,
  setThemePref as writePref,
  subscribeTheme,
  type ResolvedTheme,
  type ThemePref,
} from "@/lib/theme";

/** Read-write hook for the theme preference. State stays in sync across
 *  hook instances via window events emitted from lib/theme.ts. */
export function useTheme() {
  const [pref,     setPref]     = useState<ThemePref>(() => getThemePref());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme());

  useEffect(() => {
    return subscribeTheme(() => {
      setPref(getThemePref());
      setResolved(resolveTheme());
    });
  }, []);

  return {
    pref,
    resolved,
    setPref: (next: ThemePref) => writePref(next),
    /** Cycle through light → dark → system. */
    cycle: () => {
      const order: ThemePref[] = ["light", "dark", "system"];
      const i = order.indexOf(pref);
      writePref(order[(i + 1) % order.length]);
    },
    /** Convenience: flip light ↔ dark, treating "system" as the current
     *  resolved value (so the first click on a system-preferring user
     *  pins the opposite of what they're currently seeing). */
    toggle: () => writePref(resolved === "dark" ? "light" : "dark"),
    apply: applyTheme,
  };
}
